import React, { useEffect, useState } from 'react';
import { ProductCodeKey, SubscriptionPlan, SubscriptionPlanCode } from '@dayetopia/types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import ProductBuyOptionHeading from '@components/products/ProductBuyOptionHeading';
import ProductPromotion from '@components/products/ProductPromotion';
import { useShoppingContext } from '@components/products/ProductShoppingContext';
import AddToCart from '@components/shared/AddToCart';
import Colors from '@components/shared/Colors';
import MultiMonthOptions from '@components/shared/subscription/MultiMonthOptions';
import ScreeningFrequencyModal from '@components/storefront/ScreeningFrequencyModal';
import { BoxState, CartItem, CartItemType, IntervalUnit, PadsBoxStackState, PadsBoxState, Prices, ProductCode, SubscriptionType } from '@contracts';
import SelectedRadio from '@images/selected-radio.svg';
import UnselectedRadio from '@images/unselected-radio.svg';
import { createCartItem } from '@utils/cart';
import PriceDisplay from './PriceDisplay';
import { StyledSimpleBoxWrapper } from './SharedStyles';
import TextLinkButton from './TextLinkButton';

const defaultSubscription: SubscriptionPlan = {
  productCode: ProductCodeKey.Proviotics,
  planCode: SubscriptionPlanCode.Proviotics3Month,
  subscriptionType: SubscriptionType.Proviotics,
  basePrice: 29.9,
  discountPercent: { default: 15 },
  subscriptionInterval: { unit: IntervalUnit.Months, length: 3 },
  billing: { installments: 1 },
  delivery: { installments: 1 },
  discountedPrice: 9
};

interface Props {
  productCode: ProductCode | string;
  subscriptions: SubscriptionPlan[];
  quantity?: number;
  priceboxColor?: string;
  price: number;
  color?: string;
  prices?: Prices;
  boxState?: BoxState | PadsBoxState | PadsBoxStackState;
  buyOneText?: string;
  showMultimonth?: boolean;
  checkoutButton?: boolean;
  layout?: string;
  paymentOptionsHeader?: string;
  oneOffSubtext?: string;
}

export enum TypeOfPurchase {
  OneOffPurchase = 'one-off',
  SubscriptionPurchase = 'subscription'
}

const PurchaseOptions: React.FC<Props> = ({
  productCode,
  subscriptions,
  price,
  color,
  prices,
  boxState,
  buyOneText,
  showMultimonth = true,
  checkoutButton = false,
  layout = '0',
  paymentOptionsHeader = 'components.payment-options',
  oneOffSubtext = undefined
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(showMultimonth ? TypeOfPurchase.SubscriptionPurchase : TypeOfPurchase.OneOffPurchase);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan>(defaultSubscription);
  const isVMS = productCode === ProductCode.ScreeningKit;
  const [screeningFrequencyModalOpen, setScreeningFrequencyModalOpen] = useState(false);
  const purchaseQuantity = 1;
  const cartItemEditable = selected === TypeOfPurchase.OneOffPurchase;
  const { selectedAddonCartItems, selectedOptionCartItem, setSelectedOptionCartItem } = useShoppingContext();

  useEffect(() => {
    const defaultPlan = subscriptions.find((p: SubscriptionPlan) => p.isDefault);
    const selectedSubPlan = subscriptions.find((p: SubscriptionPlan) => selectedPlan.planCode === p.planCode);
    const newPlan = selectedSubPlan || defaultPlan || subscriptions[0];

    if (newPlan) {
      setSelectedPlan(newPlan);
    }
  }, [subscriptions]);

  useEffect(() => {
    const selectedCartItem = generateCartItem();

    if (selectedCartItem) {
      setSelectedOptionCartItem(selectedCartItem);
    }
  }, [selected, productCode, purchaseQuantity, cartItemEditable, prices, subscriptions, selectedPlan]);

  const selectOneOff = async () => {
    setSelected(TypeOfPurchase.OneOffPurchase);
  };

  const getPerItemText = () => {
    if (!showMultimonth || productCode === ProductCode.Pads || productCode === ProductCode.Proviotics) return '';
    if (productCode === ProductCode.ScreeningKit) return t('components.subscription-options.per-test');
    return t('components.subscription-options.per-pack');
  };

  const getCartItemBoxState = () => {
    if (productCode === ProductCode.ScreeningKit) {
      return { vms_essential: selected === TypeOfPurchase.OneOffPurchase ? 1 : selectedPlan.delivery.installments };
    }

    return boxState;
  };

  const generateCartItem = () => {
    const cartItemBoxState = getCartItemBoxState();
    const cartItemPrice = Number(price?.toFixed(2));
    const cartItem = createCartItem(
      productCode as ProductCode,
      purchaseQuantity,
      cartItemPrice,
      cartItemEditable,
      selected === TypeOfPurchase.OneOffPurchase ? CartItemType.OneOffPurchase : CartItemType.Subscription,
      prices,
      cartItemBoxState,
      subscriptions,
      selectedPlan
    );
    return cartItem;
  };

  const handleHelpLinkRender = () => {
    if (isVMS && showMultimonth) {
      return (
        <>
          <TextLinkButton style={{ margin: '25px auto 5px', color: Colors.navyBlue }} onClick={() => setScreeningFrequencyModalOpen(true)}>
            {t('components.how-many-screenings')}
          </TextLinkButton>
          <ScreeningFrequencyModal modalOpen={screeningFrequencyModalOpen} closeModal={() => setScreeningFrequencyModalOpen(false)} />
        </>
      );
    }
    return null;
  };

  return (
    <Container $color={color}>
      {showMultimonth && (
        <ProductBuyOptionHeading
          textKey={paymentOptionsHeader}
          onClick={isVMS ? () => setScreeningFrequencyModalOpen(true) : undefined}
          marginBottom="0"
        />
      )}
      <PurchaseOptionsWrap>
        {showMultimonth && (
          <MultiMonthOptions
            showRadioButton={true}
            selected={selected === TypeOfPurchase.SubscriptionPurchase}
            setSelected={() => setSelected(TypeOfPurchase.SubscriptionPurchase)}
            setSelectedProductCallback={setSelectedPlan}
            productCode={productCode as ProductCode}
            layout={layout}
            subscriptions={subscriptions}
            defaultSubscription={selectedPlan}
            optionsDescription={productCode === ProductCodeKey.Proviotics ? t(`components.subscription-options.best-results`) : ''}
            textColor={color}
          />
        )}
        <OptionWrap $isSelected={selected === TypeOfPurchase.OneOffPurchase} onClick={selectOneOff} $selectable $isRadioSelection>
          <input hidden type="radio" value={TypeOfPurchase.OneOffPurchase} />
          <RadioIndicator>{selected === TypeOfPurchase.OneOffPurchase ? <SelectedRadio /> : <UnselectedRadio />}</RadioIndicator>
          <PurchaseOptionTextWrap>
            <OptionDescription>
              <PurchaseDescription $textColor={color}>{buyOneText ?? t('components.one-time-purchase')}</PurchaseDescription>
              <OptionSubLabel>{oneOffSubtext}</OptionSubLabel>
            </OptionDescription>
            <Price $color="transparent" className={productCode}>
              <PriceDisplay price={price} />
              <span>{getPerItemText()}</span>
            </Price>
          </PurchaseOptionTextWrap>
        </OptionWrap>
      </PurchaseOptionsWrap>
      <ProductPromotion productId={productCode} />
      <AddToCartButtonSection $marginTop={showMultimonth ? '8px' : '0'}>
        <AddToCartStyled
          cartItem={selectedOptionCartItem}
          checkoutRedirect={checkoutButton}
          buttonText={checkoutButton ? t('components.checkout-now') : ''}
          addonCartItems={selectedAddonCartItems}
        />
      </AddToCartButtonSection>
      {handleHelpLinkRender()}
    </Container>
  );
};

export default PurchaseOptions;

const Container = styled.div<{ $color?: string }>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 100%;
  color: ${(props) => props.$color ?? Colors.forestGreen};
  gap: 16px;

  svg .color-stroke {
    stroke: ${(props) => props.$color ?? Colors.forestGreen};
  }

  svg .color-fill {
    fill: ${(props) => props.$color ?? Colors.forestGreen};
  }

  @media (max-width: 768px) {
    margin: 0 auto;

    & > button {
      padding: 0 15px;
    }
  }
`;

const PurchaseOptionsWrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
`;

const PurchaseOptionTextWrap = styled.div`
  width: 100%;
  padding: 0 0 0 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PurchaseDescription = styled.p<{ $textColor?: string }>`
  font-family: BrownStd;
  font-size: 16px;
  line-height: 26px;
  font-style: normal;
  font-weight: 700;
  text-align: left;
  color: ${(props) => props.$textColor};

  @media (max-width: 1024px) {
    font-size: 13px;
    line-height: 150%;
  }
`;

interface IPrice {
  $color: string;
}

export const OptionDescription = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  flex: none;
  flex-grow: 0;
`;

export const OptionSubLabel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  gap: 5px;
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;
  @media (max-width: 1024px) {
    font-size: 12px;
    line-height: 150%;
  }
`;

export const Price = styled.div<IPrice>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  padding: 0;
  height: 100%;
  text-align: center;
  color: inherit;
  background: ${(props) => (props.$color !== '' ? props.$color : `${Colors.paleGold50}`)};

  &.screening_kit {
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
  }

  & > div {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: flex-start;
    padding: 0;
    flex: none;
    font-family: 'Domaine';
    font-style: normal;
    font-weight: normal;
    font-size: 18px;
    line-height: 110%;
    color: inherit;
  }
`;

export const PriceModern = styled.div`
  font-family: 'Domaine';
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 110%;
  display: flex;
  align-items: center;
  text-align: center;
  color: ${Colors.navyBlue};

  sup {
    top: -2px;
    font-size: 12px;
    margin-left: 2px;
  }
`;

const OptionWrap = styled(StyledSimpleBoxWrapper)`
  display: flex;
  max-width: 100%;
  padding: 16px;
`;

const RadioIndicator = styled.div`
  align-self: center;
  width: 20px;
  height: 20px;
`;

const AddToCartButtonSection = styled.div<{ $marginTop: string }>`
  margin-top: ${(p) => p.$marginTop};
  width: 100%;
  display: flex;
  justify-content: end;
  flex-direction: column;
  gap: 10px;
`;

const AddToCartStyled = styled(AddToCart)`
  background-color: #d06baa;
  width: 100%;
`;
